// @ts-check

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, BlockStack, Button, Card, Image, Layout, Page, Spinner, Text } from "@shopify/polaris";

import { usePostHog } from "posthog-js/react";

import { AdCampaignsTable } from "components/AdCampaignsTable/AdCampaignsTable";
import { LockableCard } from "components/cards/LockableCard/LockableCard";
import { FetchOrdersControls } from "components/FetchOrderControls/FetchOrdersControls";

import { useGlobalContext } from "context/GlobalContext";
import { usePlatforms } from "context/PlatformsContext";

import adCampaignsFeatureScreenshot from "assets/ad-campaigns-screenshot.png";

export default function AdCampaignsPage() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const {
    locked,
    store,
    refetchStore,
    isFetchingStore,
    refetchAdCampaigns,
    isFetchingAdCampaigns,
  } = useGlobalContext();
  const {
    loading,
    setupSteps,
    isFetchingConnectablePlatforms,
  } = usePlatforms();

  const [betaAccessLoading, setBetaAccessLoading] = useState(false);

  const featureFlagEnabled = store && store.subscription?.feature_flags?.facebook_link === true;
  const finishedSetup = setupSteps.every(({ complete }) => complete);

  useEffect(() => {
    posthog.capture("$pageview");
  }, []);

  useEffect(() => {
    if (!loading && featureFlagEnabled && !finishedSetup) {
      navigate("/ad-campaigns/setup");
    }
  }, [loading, featureFlagEnabled, finishedSetup, navigate]);

  const handleUpgradeClick = () => {
    navigate("/settings/plans");
  };

  let body;
  let controls;
  let errorBanner;

  if (isFetchingStore || isFetchingConnectablePlatforms) {
    body = (
      <Card>
        <Spinner size="small" />
      </Card>
    );
  } else if (featureFlagEnabled) {
    if (finishedSetup) {
      body = <AdCampaignsTable />;

      controls = (
        <FetchOrdersControls
          loading={isFetchingAdCampaigns}
          onRefresh={refetchAdCampaigns}
        />
      );
    }
  } else {
    body = (
      <Card>
        <BlockStack gap="600" inlineAlign="center">
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd" alignment="center">Upgrade your plan to access this feature</Text>

            <Text as="p" variant="bodyMd" alignment="center">
              Unlock details insights on your Meta ads including CAC, ROAS, and more.
            </Text>
          </BlockStack>

          <Button variant="primary" onClick={handleUpgradeClick}>
            Upgrade now
          </Button>

          <Image
            source={adCampaignsFeatureScreenshot}
            alt={"Ad campaigns feature"}
            width={800}
          />
        </BlockStack>
      </Card>
    );
  }

  return (
    <Page
      title="Ad campaigns"
      fullWidth
    >
      <Layout>
        {errorBanner}

        <Layout.Section>
          <BlockStack gap="200">
            {controls}

            <LockableCard locked={locked}>
              {body}
            </LockableCard>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};
